const MESSAGE_KEYS = [
  "message",
  "messages",
  "error",
  "detail",
  "title",
  "description",
];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const normalizeMessage = (value: unknown): string | undefined => {
  if (typeof value !== "string") return undefined;

  const message = value.trim();
  return message.length > 0 ? message : undefined;
};

export const extractApiErrorMessages = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.flatMap(extractApiErrorMessages);
  }

  const message = normalizeMessage(value);
  if (message) return [message];

  if (!isRecord(value)) return [];

  const directMessages = MESSAGE_KEYS.flatMap((key) =>
    extractApiErrorMessages(value[key]),
  );

  if (directMessages.length > 0) return directMessages;

  return Object.values(value).flatMap(extractApiErrorMessages);
};

export const getApiErrorDataMessage = (
  data: unknown,
  fallbackMessage: string,
): string => {
  if (typeof data === "string") {
    return normalizeMessage(data) ?? fallbackMessage;
  }

  if (Array.isArray(data)) {
    const dataMessages = extractApiErrorMessages(data);
    return dataMessages.length > 0 ? dataMessages.join(", ") : fallbackMessage;
  }

  if (!isRecord(data)) return fallbackMessage;

  const responseMessages = [
    ...extractApiErrorMessages(data.errors),
    ...extractApiErrorMessages(data.message),
    ...extractApiErrorMessages(data.messages),
    ...extractApiErrorMessages(data.error),
    ...extractApiErrorMessages(data.detail),
    ...extractApiErrorMessages(data.title),
  ];

  if (data.data !== data) {
    const nestedDataMessage = getApiErrorDataMessage(data.data, "");
    if (nestedDataMessage) responseMessages.push(nestedDataMessage);
  }

  const uniqueMessages = Array.from(new Set(responseMessages));
  return uniqueMessages.length > 0
    ? uniqueMessages.join(", ")
    : fallbackMessage;
};

const getApiErrorMessage = (error: any, fallbackMessage: string): string => {
  if (error?.response) {
    return getApiErrorDataMessage(error.response.data, fallbackMessage);
  }

  return normalizeMessage(error?.message) ?? fallbackMessage;
};

export default getApiErrorMessage;
